/**
 * Live View HUD — overlays the editor map while a mission is in flight.
 *
 * Renders the chrome (top status bar, telemetry strip, close button) plus
 * the empty-state "Awaiting operator telemetry" message that's shown until
 * a real telemetry stream is wired in.
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ [LIVE]  Mission name · Dock A                              [×]   │ ← top status bar
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │                                                                  │
 *   │           Awaiting operator telemetry                            │
 *   │                                                                  │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ Alt —  ·  H — m/s  ·  V — m/s  ·  Hdg —°  ·  Battery —%          │ ← telemetry strip
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * The component is purely presentational. The map drone marker is owned by
 * useDemoLiveView (which manages its source/layer lifecycle but leaves the
 * data empty until real telemetry arrives).
 */

import { useEffect } from 'react';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import { useIsLiveViewOpen } from './useDemoLiveView';
import './LiveViewMockup.css';

export default function LiveViewMockup() {
  const open = useIsLiveViewOpen();

  const liveViewMissionId = useDockDemoStore((s) => s.liveViewMissionId);
  const missions = useDockDemoStore((s) => s.missions);
  const dockStations = useDockDemoStore((s) => s.dockStations);
  const closeLiveView = useDockDemoStore((s) => s.closeLiveView);

  // ESC closes the live view — same affordance every modal in the app uses.
  // Listener mounts only while open so we don't trap ESC for closed live view.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLiveView();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeLiveView]);

  if (!open) return null;

  const mission = missions.find((m) => m.id === liveViewMissionId);
  const dock = mission?.dockStationId
    ? dockStations.find((d) => d.id === mission.dockStationId)
    : null;
  if (!mission || !dock) return null;

  // Placeholder values for the telemetry strip — a real-time stream
  // consumer would push live numbers in. Dashes signal "not connected yet"
  // without breaking the layout.
  const DASH = '—';

  return (
    <div className="live-view-mockup" role="dialog" aria-label="Live view">
      {/* ─── Top status bar ──────────────────────────────────────── */}
      <div className="live-view-mockup__bar live-view-mockup__bar--top">
        <div className="live-view-mockup__bar-row">
          <span className="live-view-mockup__live-pill">
            <span className="live-view-mockup__rec-dot" /> LIVE
          </span>
          <span className="live-view-mockup__title">
            <strong>{mission.name}</strong>
            <span className="live-view-mockup__sep">·</span>
            <span>{dock.name || '(unnamed dock)'}</span>
          </span>
          <button
            type="button"
            className="live-view-mockup__close"
            onClick={closeLiveView}
            aria-label="Close live view"
            title="Close (Esc)"
          >
            ×
          </button>
        </div>
      </div>

      {/* ─── Center empty-state message ─────────────────────────── */}
      <div className="live-view-mockup__empty">
        Awaiting operator telemetry
      </div>

      {/* ─── Bottom telemetry strip ─────────────────────────────── */}
      <div className="live-view-mockup__bar live-view-mockup__bar--bottom">
        <div className="live-view-mockup__bar-row">
          <Stat label="Alt" value={DASH} />
          <Sep />
          <Stat label="H Speed" value={DASH} />
          <Sep />
          <Stat label="V Speed" value={DASH} />
          <Sep />
          <Stat label="Heading" value={DASH} />
          <Sep />
          <Stat label="Battery" value={DASH} />
          <Sep />
          <Stat label="GPS" value={DASH} />
          <Sep />
          <Stat label="Signal" value={DASH} />
        </div>
      </div>
    </div>
  );
}

/* ─── Small presentational helpers ──────────────────────────────── */

interface StatProps {
  label: string;
  value: string;
}
function Stat({ label, value }: StatProps) {
  return (
    <span className="live-view-mockup__stat">
      <span className="live-view-mockup__stat-label">{label}</span>
      <span className="live-view-mockup__stat-value">{value}</span>
    </span>
  );
}

function Sep() {
  return <span className="live-view-mockup__bar-sep" aria-hidden="true">·</span>;
}
